<script setup lang="ts">
import { equipCollection } from '@/core/equip'
import { useUserStore } from '@/stores/modules/user'
import type { EquipItemType } from '@/types/equip'
import { computed, ref } from 'vue'
import { equipIcon, equipTypeList } from '../../item/data'
import EquipInfo from '../../item/components/EquipInfo.vue'
import { getEngravePrice } from '../data'
import { socketEmitAsync } from '@/utils/web-socket'
import type { EngraveRes } from '@/types/smithy'
import { formatNumberWithCommas } from '@/utils'
import { ElMessage } from 'element-plus'
import { getEquipName } from '@/utils/equip'
import { QuestionFilled } from '@element-plus/icons-vue'
import { explainDarkGoldEquipAffix } from '@/core/equip/dark-gold'
import { cloneDeep } from 'lodash'
import type { UserAttrType } from '@/types/user'

const userStore = useUserStore()

const equipItemId = ref('')
const equipInfo = computed(() => {
  const res = userStore.itemList.find((item) => item.id === equipItemId.value)
  if (!res) {
    equipItemId.value = ''
    return undefined
  }
  const { name, type, attrs } = equipCollection[res.equipId]
  return { ...res, name, type, attrs }
})
const equippedList = computed(() =>
  equipTypeList.value.map((item) => {
    const itemId = userStore.equippedList[item]
    const equipInfo = userStore.itemList.find((item) => item.id === itemId)
    if (!equipInfo) return undefined
    const { name, type, attrs } = equipCollection[equipInfo.equipId]
    return { ...equipInfo, name, type, attrs }
  }),
)

const itemList = computed<EquipItemType[]>(() => {
  const items = userStore.itemList
  if (userStore.itemList.length === 0) return []
  return items
    .filter((item) => {
      const equippedIdList = Object.values(userStore.equippedList)
      if (!item.darkGoldAttrs) return false
      return !equippedIdList.includes(item.id)
    })
    .map((item) => {
      const { equipId } = item
      const { name, type, attrs } = equipCollection[equipId]
      return { ...item, name, type, attrs }
    })
})

function handleChoice(item: EquipItemType | undefined) {
  if (item && !item.darkGoldAttrs) return ElMessage.warning('仅暗金装备可刻印')
  equipItemId.value = item?.id || ''
  useCoupon.value = false
}

const useCoupon = ref(false)
function beforeUseCouponChange() {
  if (!userStore.consumables.engraveCoupon) {
    ElMessage.warning('您未拥有刻印券')
    return false
  }
  return true
}
const resShow = ref(false)
const resEquipInfo = ref()
const isRecord = ref(false)
async function handleEngrave() {
  if (!equipInfo.value) return
  const { id, engraveAttr } = equipInfo.value
  const price = getEngravePrice(engraveAttr?.count)

  const {
    engraveAttr: resEngraveAttr,
    userAttr,
    isRecord: resIsRecord,
  } = await socketEmitAsync<EngraveRes>({ type: 'engraveEquip', data: { id, useCoupon: useCoupon.value } })
  resEquipInfo.value = undefined
  isRecord.value = resIsRecord
  if (userAttr) userStore.userAttr = userAttr
  if (!resIsRecord) {
    if (useCoupon.value) {
      userStore.consumables.engraveCoupon--
      if (userStore.consumables.engraveCoupon <= 0) useCoupon.value = false
    } else {
      userStore.gold -= price
    }
  }

  const item = userStore.itemList.find((item) => item.id === id)
  if (!item || !equipInfo.value) return
  if (resEngraveAttr?.count === 1) {
    item.engraveAttr = resEngraveAttr
    equipInfo.value.engraveAttr = resEngraveAttr
  } else {
    resEquipInfo.value = {
      ...cloneDeep(equipInfo.value),
      engraveAttr: resEngraveAttr,
    }
  }

  resShow.value = true
}
async function handleChoiceEngraveRes(type: 0 | 1) {
  ElMessageBox.confirm(
    `<p class="affix">确定<span class="info">${type === 0 ? '保留' : '覆盖'}</span>刻印吗？（该操作将<span class="shrink">无法撤销</span>）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      if (!equipInfo.value || !resEquipInfo.value) return
      const itemId = equipInfo.value.id
      const userAttr = await socketEmitAsync<UserAttrType>({ type: 'choiceEngraveRes', data: { itemId, choiceIndex: type } })
      const item = userStore.itemList.find((item) => item.id === itemId)
      if (!item) return
      if (userAttr) {
        userStore.userAttr = userAttr

        const resEngraveAttr = resEquipInfo.value.engraveAttr
        item.engraveAttr = resEngraveAttr
        equipInfo.value.engraveAttr = resEngraveAttr
      } else {
        if (!item.engraveAttr || !equipInfo.value.engraveAttr) return
        item.engraveAttr.count++
      }

      resShow.value = false
      ElMessage.success('刻印确认成功')
    })
    .catch(() => {})
}
</script>

<template>
  <div class="enchant">
    <div class="enchant-table border-wrap">
      <h4>锻造台</h4>
      <div class="enchant-equip">
        <CommonBtn style="width: 100%" @done="handleChoice(undefined)">
          <span v-if="equipInfo" v-html="getEquipName(equipInfo, equipInfo.type)"></span>
          <span v-else>(未放入)</span>
        </CommonBtn>
      </div>
      <div class="help-info">
        <el-popover placement="bottom" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>刻印说明</h4>
            <p>1. 通过<span class="info">刻印</span>，可为装备附加一条随机的<span class="info">特殊属性</span>。</p>
            <p>2. 刻印属性的特殊词条不会与暗金属性中的特殊词条一致。</p>
            <p>
              3. 首次刻印<span class="grow">免费</span>，而后刻印花费将随<span class="info">刻印次数</span>逐步增加，直至
              <span class="grow">10次</span>时达到最大值。
            </p>
            <p>4. 第二次刻印开始，可自主选择保留/覆盖刻印词条，若您忘记选择，可对再次对该装备刻印以继续选择。</p>
            <p>5. 各个部位可产出的词条如下：</p>
            <p style="text-indent: 2em">a. {{ equipIcon.weapon }}：破阵、冲锋、收割、冲击、重击、碎骨、轻灵、爆发、分裂、裂创、残忍</p>
            <p style="text-indent: 2em">
              b. {{ equipIcon.helmet }}：破阵、冲锋、收割、冲击、重击、碎骨、爆发、追击、重创、影刃、裂创、精准、击溃、残忍
            </p>
            <p style="text-indent: 2em">
              c. {{ equipIcon.armor }}：破阵、冲锋、收割、收尾、轻灵、重击、碎骨、分裂、追击、重创、虚无、精准、击溃、残忍
            </p>
            <p style="text-indent: 2em">
              d. {{ equipIcon.shoes }}：冲击、收尾、轻灵、重击、碎骨、爆发、分裂、追击、重创、影刃、精准、裂创、虚无、击溃
            </p>
            <p style="text-indent: 2em">e. {{ equipIcon.jewelry }}：收尾、轻灵、分裂、追击、重创、影刃、裂创、虚无、精准、爆发、残忍</p>
          </div>
        </el-popover>
      </div>
      <template v-if="equipInfo">
        <div class="info-card">
          <p style="display: flex; align-items: center">
            <span>刻印次数：{{ equipInfo.engraveAttr?.count || 0 }}</span>
            <span
              >使用刻印券（{{ userStore.consumables.engraveCoupon }}）：<el-switch
                v-model="useCoupon"
                size="small"
                :before-change="beforeUseCouponChange"
            /></span>
          </p>
          <p>刻印花费：{{ useCoupon ? 0 : formatNumberWithCommas(getEngravePrice(equipInfo.engraveAttr?.count)) }} 金币</p>
          <template v-if="equipInfo.engraveAttr">
            <p style="display: block">
              刻印词条：
              <span class="affix" v-for="item of explainDarkGoldEquipAffix(equipInfo.engraveAttr.special, false)" v-html="item"></span>
            </p>
          </template>
        </div>
        <div class="btn-wrap">
          <el-button type="warning" plain @click="handleEngrave"> 开始刻印 </el-button>
        </div>
      </template>
    </div>
    <div class="item-list border-wrap" style="flex-shrink: 0; margin-bottom: 10px">
      <h4>已装备</h4>
      <div class="item-btn-wrap btn-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="(item, index) of equippedList" :key="index">
            <CommonBtn
              size="small"
              @done="handleChoice(item)"
              :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
              :type="item ? 'primary' : 'info'"
            >
              <span v-if="item" v-html="getEquipName(item, index)"></span>
              <span v-else>{{ equipIcon[equipTypeList[index]] }} (未装备)</span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>
    <div class="item-list border-wrap package">
      <h4>背包</h4>
      <p class="cur-gold">
        <span>💰 {{ formatNumberWithCommas(userStore.gold) }}</span>
      </p>
      <div class="item-btn-wrap btn-wrap item-list-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="item of itemList" :key="item.id">
            <CommonBtn size="small" @done="handleChoice(item)" :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }">
              <span v-html="getEquipName(item, item.type)"></span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>

    <el-dialog v-model="resShow" width="100%" style="max-width: 500px" :before-close="() => false">
      <h2 class="affix">
        <span class="grow">{{ isRecord ? '上次刻印结果未确认' : '刻印成功' }}</span>
      </h2>
      <p style="text-align: center" v-show="resEquipInfo">请选择保留的属性</p>
      <div class="engrave-res">
        <div class="left border-wrap" v-if="equipInfo">
          <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
            <template #reference>
              <div>
                <CommonBtn size="small" style="width: 100%">
                  <span v-html="getEquipName(equipInfo, equipInfo.type)"></span>
                </CommonBtn>
              </div>
            </template>
            <EquipInfo :equip-info="equipInfo" :show-action="false" style="max-height: 50vh" />
          </el-popover>
          <template v-if="equipInfo.engraveAttr">
            <p class="affix" v-for="item of explainDarkGoldEquipAffix(equipInfo.engraveAttr.special, false)" v-html="item"></p>
          </template>
          <div class="btn-wrap" v-show="resEquipInfo">
            <el-button type="primary" plain size="small" @click="handleChoiceEngraveRes(0)">保留</el-button>
          </div>
        </div>
        <div class="right border-wrap" v-if="resEquipInfo">
          <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
            <template #reference>
              <div>
                <CommonBtn size="small" style="width: 100%">
                  <span v-html="getEquipName(equipInfo, resEquipInfo.type)"></span>
                </CommonBtn>
              </div>
            </template>
            <EquipInfo :equip-info="resEquipInfo" :show-action="false" style="max-height: 50vh" />
          </el-popover>
          <template v-if="resEquipInfo.engraveAttr">
            <p class="affix" v-for="item of explainDarkGoldEquipAffix(resEquipInfo.engraveAttr.special, false)" v-html="item"></p>
          </template>
          <div class="btn-wrap">
            <el-button type="warning" plain size="small" @click="handleChoiceEngraveRes(1)">覆盖</el-button>
          </div>
        </div>
      </div>
      <template #footer>
        <p style="text-align: center" v-show="!resEquipInfo">
          <el-button plain @click="resShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.enchant {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  .enchant-table {
    position: relative;
    padding: 0 8px 10px 8px;
    margin-bottom: 10px;
    overflow: auto;
    .enchant-equip {
      border: 1px var(--fontColor) solid;
      padding: 6px 8px;
      border-radius: 4px;
      background-color: rgba($color: var(--fontColor), $alpha: 0.1);
    }
    .help-info {
      position: absolute;
      top: 5px;
      right: 10px;
    }
  }
  .item-list {
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    justify-content: unset;
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
    .cur-gold {
      width: 100%;
      text-align: right;
      font-size: 14px;
      padding-right: 10px;
      margin: 0;
    }
    .item-btn-wrap {
      overflow: auto;
    }
    &.package {
      flex: 1;
      min-height: 135px;
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
  .engrave-res {
    display: flex;
    justify-content: space-between;
    > div {
      width: calc(50% - 5px);
      margin: 0 auto;
      flex: 1;
    }
    .border-wrap {
      padding: 10px;
    }
  }
}
</style>
